import { useState, useEffect, useMemo } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import api from "../../api";
import SnackBarComponent from "../../components/SnackBarComponent";
import EditIcon from '@mui/icons-material/Edit';
import { useDebounce } from "../../hooks/useDebounce";

import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const headCells = [
  { id: "Name", numeric: false, disablePadding: true, label: "Name" },
  { id: "CreatedTimestamp", numeric: false, disablePadding: false, label: "Created At" },
  { id: "Edit", numeric: false, disablePadding: false, label: "Edit" },
];

function EnhancedTableHead(props) {
  const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
  const createSortHandler = (property) => (event) => onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function EnhancedTableToolbar(props) {
  const { numSelected, handleAdd, handleDelete, searchQuery, setSearchQuery } = props;
  const theme = useTheme();

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        ...(numSelected > 0 && {
          bgcolor: alpha(theme.palette.primary.main, theme.palette.action.activatedOpacity),
        }),
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          {numSelected} selected
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
          <Typography sx={{ flexShrink: 0 }} variant="h6" id="tableTitle" component="div">
            MAIL TEMPLATES
          </Typography>

          <TextField
            variant="outlined"
            size="small"
            placeholder="Search Templates..."
            value={searchQuery || ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ ml: 'auto', width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {numSelected === 1 ? (
        <Box sx={{ display: 'flex', ml: 2 }}>
          <Tooltip title="Delete">
            <IconButton onClick={handleDelete}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', ml: 2 }}>
          {numSelected > 0 ? (
            <Tooltip title="Delete">
              <IconButton onClick={handleDelete}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="New Template">
              <IconButton onClick={handleAdd}>
                <AddIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
    </Toolbar>
  );
}

export default function MailTemplates() {
  const [rows, setRows] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Name");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [dense, setDense] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 300);

  // Upload State
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [jsonInput, setJsonInput] = useState("");
  const [jsonError, setJsonError] = useState(false);
  const [jsonHelperText, setJsonHelperText] = useState("");
  const [forceUpdate, setForceUpdate] = useState(false);
  const [uploading, setUploading] = useState(false);

  // NEW: State to track editing
  const [editingTemplateName, setEditingTemplateName] = useState(null);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [errorMessage, setErrorMessage] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    const token = localStorage.getItem("accessToken");
    try {
      const response = await api.get("/api/mail-templates", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRows(response.data.data || []);
      setIsLoading(false);
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Error fetching templates");
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredRows = useMemo(() => {
    if (!debouncedSearchQuery) return rows;
    const lowerQuery = debouncedSearchQuery.toLowerCase();
    return rows.filter((row) =>
      (row.Name?.toLowerCase() || "").includes(lowerQuery)
    );
  }, [rows, debouncedSearchQuery]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  const handleAdd = () => {
    setOpenUploadDialog(true);
    setJsonInput("");
    setJsonError(false);
    setJsonHelperText("");
    setForceUpdate(false);
    setEditingTemplateName(null); // Clear editing state
  };

  const handleJsonChange = (e) => {
    setJsonInput(e.target.value);
    if (jsonError) {
      setJsonError(false);
      setJsonHelperText("");
    }
  };

  const handleFileImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const obj = JSON.parse(text);
        setJsonInput(JSON.stringify(obj, null, 2));
        setJsonError(false);
        setJsonHelperText("");

        setSnackbarMessage("File imported successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      } catch (err) {
        setJsonError(true);
        setJsonHelperText("Invalid JSON file: " + err.message);
        setSnackbarMessage("Failed to parse JSON file");
        setSnackbarSeverity("error");
        setOpenSnackbar(true);
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBeautify = () => {
    if (!jsonInput.trim()) return;
    try {
      const obj = JSON.parse(jsonInput);
      setJsonInput(JSON.stringify(obj, null, 2));
      setJsonError(false);
      setJsonHelperText("");
    } catch (e) {
      setJsonError(true);
      setJsonHelperText("Invalid JSON: " + e.message);
      setSnackbarMessage("Invalid JSON format");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
    }
  };

  const handleEdit = async (templateName) => {
    const token = localStorage.getItem("accessToken");
    try {
      const response = await api.get(`/api/mail-templates/${templateName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let templateData = response.data.data || response.data;
      templateData = { Template: templateData };
      setJsonInput(JSON.stringify(templateData, null, 2));
      setEditingTemplateName(templateName);
      setForceUpdate(true); // Usually edits imply overwriting
      setOpenUploadDialog(true);
    } catch (err) {
      setSnackbarMessage("Failed to fetch template details");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleUploadSubmit = async () => {
    if (!jsonInput.trim()) {
      setSnackbarMessage("Please enter JSON content");
      setSnackbarSeverity("warning");
      setOpenSnackbar(true);
      return;
    }

    let parsedJson;
    try {
      parsedJson = JSON.parse(jsonInput);
    } catch (e) {
      setJsonError(true);
      setJsonHelperText("Invalid JSON syntax. Please correct it.");
      return;
    }

    if (!parsedJson.Template) {
      setJsonError(true);
      setJsonHelperText("Invalid Format: Root object must contain a 'Template' key.");
      return;
    }

    const { TemplateName, SubjectPart, HtmlPart } = parsedJson.Template;

    if (TemplateName === undefined || SubjectPart === undefined || HtmlPart === undefined) {
      setJsonError(true);
      setJsonHelperText("Invalid Template: 'Template' must include 'TemplateName', 'SubjectPart', and 'HtmlPart'.");
      return;
    }

    setUploading(true);
    const token = localStorage.getItem("accessToken");

    try {
      // Logic for Renaming:
      // If we are editing and the name changed, delete the old one first.
      if (editingTemplateName && editingTemplateName !== TemplateName) {
        await api.delete(`/api/mail-templates/${editingTemplateName}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      // Create / Update
      await api.post("/api/mail-templates/upload", {
        templateData: parsedJson.Template,
        forceUpdate: forceUpdate
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      setSnackbarMessage(editingTemplateName ? "Template updated successfully!" : "Template uploaded successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      setOpenUploadDialog(false);
      setEditingTemplateName(null);
      fetchData();
      setJsonInput("");
    } catch (err) {
      const msg = err.response?.data?.message || "Upload failed";
      setSnackbarMessage(msg);
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setUploading(false);
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = filteredRows.map((n) => ({ Name: n.Name }));
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    const exists = selected.find(item => item.Name === row.Name);
    let newSelected = [];
    if (exists) {
      newSelected = selected.filter(item => item.Name !== row.Name);
    } else {
      newSelected = [...selected, { Name: row.Name }];
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (Name) => selected.some(item => item.Name === Name);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const token = localStorage.getItem("accessToken");
    try {
      await Promise.all(selected.map((item) =>
        api.delete(`/api/mail-templates/${item.Name}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ));

      const deletedNames = selected.map(s => s.Name);
      setRows(prevRows => prevRows.filter(r => !deletedNames.includes(r.Name)));

      setSelected([]);
      setOpenDeleteDialog(false);
      setSnackbarMessage("Deleted successfully!");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

    } catch (error) {
      console.error("Delete Failed", error);
      setSnackbarMessage(error.response?.data?.message || "Failed to delete");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = (event, reason) => { if (reason === "clickaway") return; setOpenSnackbar(false); };
  const handleDeleteCancel = () => setOpenDeleteDialog(false);
  const handleUploadClose = () => {
    setOpenUploadDialog(false);
    setEditingTemplateName(null); // Reset edit state on close
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const visibleRows = useMemo(() =>
    [...filteredRows]
      .sort(getComparator(order, orderBy))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [order, orderBy, page, rowsPerPage, filteredRows]
  );

  return (
    <Box sx={{ width: "100%" }}>
      {openSnackbar && <SnackBarComponent message={snackbarMessage} severity={snackbarSeverity} onClose={handleCloseSnackbar} />}

      <Paper sx={{ width: "100%", mb: 2 }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          handleAdd={handleAdd}
          handleDelete={handleDeleteClick}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
        />
        <TableContainer>
          <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle" size={dense ? "small" : "medium"}>
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={filteredRows.length}
            />
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={3} align="center"><CircularProgress size="2rem" /></TableCell></TableRow>
              ) : visibleRows.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center"><Alert severity="info">{errorMessage || "No templates found."}</Alert></TableCell></TableRow>
              ) : (
                visibleRows.map((row, index) => {
                  const isItemSelected = isSelected(row.Name);
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      onClick={(event) => handleClick(event, row)}
                      role="checkbox"
                      aria-checked={isItemSelected}
                      tabIndex={-1}
                      key={row.Name}
                      selected={isItemSelected}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox color="primary" checked={isItemSelected} inputProps={{ "aria-labelledby": labelId }} />
                      </TableCell>
                      <TableCell id={labelId}>{row.Name}</TableCell>
                      <TableCell>{formatDate(row.CreatedTimestamp)}</TableCell>

                      <TableCell>
                        <Tooltip title="Edit">
                          {/* CHANGED: Edit Button logic */}
                          <IconButton onClick={(e) => { e.stopPropagation(); handleEdit(row.Name); }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* --- UPLOAD / EDIT DIALOG --- */}
      <Dialog open={openUploadDialog} onClose={handleUploadClose} maxWidth="md" fullWidth>
        <DialogTitle>{editingTemplateName ? "Edit Template" : "Upload JSON Template"}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="textSecondary">Paste JSON or Import File.</Typography>
              <Stack direction="row" spacing={1}>
                <Button component="label" size="small" variant="outlined" startIcon={<UploadFileIcon />}>
                  Import JSON
                  <input type="file" hidden accept=".json" onChange={handleFileImport} />
                </Button>
                <Button size="small" variant="outlined" startIcon={<AutoFixHighIcon />} onClick={handleBeautify}>Format</Button>
              </Stack>
            </Stack>

            <TextField
              label="Template JSON"
              multiline
              rows={12}
              value={jsonInput}
              onChange={handleJsonChange}
              fullWidth
              variant="outlined"
              error={jsonError}
              helperText={jsonHelperText}
              placeholder='{ "Template": { "TemplateName": "MyTemplate", "SubjectPart": "Hello", ... } }'
              InputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={forceUpdate}
                  onChange={(e) => setForceUpdate(e.target.checked)}
                  color="warning"
                />
              }
              label={
                <Typography variant="body1">
                  Force Update <span style={{ fontSize: '0.8em', color: 'grey' }}>(Deletes and Recreates if exists)</span>
                </Typography>
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadClose}>Cancel</Button>
          <Button
            onClick={handleUploadSubmit}
            variant="contained"
            disabled={uploading}
          >
            {uploading ? "Saving..." : (editingTemplateName ? "Update Template" : "Save Template")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete the selected {selected.length} template(s)?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}